import { useHistory } from "react-router-dom";
import { stringify } from "qs";
import merge from "lodash/merge";

import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useReducer,
} from "react";
import { useQuery } from "react-query";
import { getText } from "./utils";

const initialState = {
  pagination: {
    rowsPerPageOptions: [],
    rowsPerPage: 50,
    total: 0,
    page: 1,
  },
  isLoading: true,
  initialQuery: undefined, // TODO i could make a javascript default pagination here
  queryFunctionName: undefined,
  data: undefined,
  filters: {},
  headerExport: [],
};

const dataTableReducer = (state, action) => {
  switch (action.type) {
    case "providerStateChange":
      return {
        ...state,
        ...action.payload,
        pagination: {
          ...state.pagination,
          page: action.payload.pagination?.page || 1,
        },
        isLoading: true,
      };

    case "updateHeader":
      return {
        ...state,
        headerExport: action.payload.thead
          .filter((f) => !f.noExport)
          .map((h) => ({
            label: h.exportLabel ?? getText(h.label),
            key: h.cell.value,
          })),
      };

    case "initialize":
      return {
        ...state,
        isLoading: true,
        initialQuery: action.payload.queryFunction,
        queryFunctionName: action.payload.queryFunctionName,
        headerExport: action.payload.thead
          .filter((f) => !f.noExport)
          .map((h) => ({
            label: h.exportLabel ?? getText(h.label),
            key: h.cell.value,
          })),
        pagination: {
          ...state.pagination,
          rowsPerPage: action.payload.rowsPerPage,
        },
      };

    case "setData":
      return {
        ...state,
        data: action.payload.data,
        pagination: {
          ...state.pagination,
          total: action.payload.total,
        },
        isLoading: false,
      };

    case "setPage":
      return {
        ...state,
        pagination: { ...state.pagination, page: action.payload.page },
        isLoading: true,
      };
    case "changeFilter":
      return {
        ...state,
        pagination: {
          ...state.pagination,
          page: 1,
        },
        filters: {
          ...state.filters,
          [action.payload.name]: action.payload.value,
        },
        isLoading: true,
      };

    case "exportFile":
      return {
        ...state,
        isLoading: true,
      };

    default: {
      throw Error(`Unknown action: ${action.type}`);
    }
  }
};

export const DataTableContext = createContext(initialState);
export const DataTableContextDispatch = createContext(null);

export const useDataPagination = () => useContext(DataTableContext);
export const useDataDispatch = () => useContext(DataTableContextDispatch);

export function DataTableProvider({
  children,
  externalState = initialState,
}: {
  children: ReactNode;
  externalState?: Partial<typeof initialState>;
}) {
  const history = useHistory();

  const [dataTableState, dispatch] = useReducer(
    dataTableReducer,
    merge(initialState, externalState)
  );
  const {
    initialQuery,
    queryFunctionName,
    pagination: { page, rowsPerPage },
    filters,
  } = dataTableState;

  useEffect(() => {
    dispatch({ type: "providerStateChange", payload: externalState });
  }, [externalState]);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      queryFunctionName || "init",
      Number(page),
      Number(rowsPerPage),
      filters,
    ],
    queryFn: async () =>
      initialQuery?.({
        page,
        rowsPerPage,
        ...filters,
      }),
    enabled: !!initialQuery && !!queryFunctionName,
    staleTime: 1000 * 60,
    retry: 0,
  });

  useEffect(() => {
    if (error) {
      dispatch({
        type: "setData",
        payload: { data: [], total: 0 },
      });
    }

    if (typeof initialQuery === "function" && data && !isLoading) {
      // See when we use useHistory the page reload and the fetch is launch again
      window.history.replaceState(
        null,
        "new page title",
        `${history.location.pathname}?${stringify({
          page,
          rowsPerPage,
          ...filters,
        })}`
      );

      dispatch({
        type: "setData",
        payload: { data: data.data, total: data.total },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, filters, page, rowsPerPage, error]);

  return (
    <DataTableContext.Provider value={dataTableState}>
      <DataTableContextDispatch.Provider value={dispatch}>
        {children}
      </DataTableContextDispatch.Provider>
    </DataTableContext.Provider>
  );
}
